if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnails();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      /*
       * MARO 2026-10-18: Die Klick-Handler der Vorschaubilder haengen an jedem
       * einzelnen Element. Wer die Liste austauscht, muss sie neu binden, sonst
       * sind die Bilder sichtbar, aber tot. Darum aus dem Konstruktor
       * herausgeloest.
       */
      bindThumbnails() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      /*
       * MARO 2026-10-18: Dawns updateMedia gleicht nur die erste Liste in
       * media-gallery ab - die grosse Galerie in GalleryViewer. Die
       * Vorschaubild-Leiste in GalleryThumbnails ist eine zweite Liste und
       * blieb beim Variantenwechsel stehen. Mit dem Konfigurationsfilter faellt
       * das auf: nach dem Klick standen 16 Galeriebilder gegen 12
       * Vorschaubilder.
       *
       * Hier bewusst komplett ersetzt statt abgeglichen. Die grosse Galerie
       * wird abgeglichen, weil dort Videos und 3D-Modelle haengen, die ein
       * Neuaufbau zuruecksetzen wuerde. Vorschaubilder sind nur Knopf und Bild,
       * da ist Ersetzen einfacher und damit sicherer.
       *
       * Danach drei Dinge in dieser Reihenfolge: Handler neu binden,
       * Slider-Zustand zuruecksetzen (Dawns eigenes resetPages liest die
       * Elemente neu ein), und erst dann darf setActiveMedia laufen.
       */
      replaceThumbnails(innerHTML) {
        if (!this.elements.thumbnails) return false;
        const liste = this.elements.thumbnails.querySelector('ul');
        if (!liste || typeof innerHTML !== 'string') return false;
        if (liste.innerHTML === innerHTML) return false;
        liste.innerHTML = innerHTML;
        this.bindThumbnails();
        this.elements.thumbnails.resetPages?.();
        return true;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            /*
             * MARO 2026-10-18: Dawn sichert `activeMedia` weiter oben mit einem
             * Rueckfall UND einem Abbruch ab, `activeThumbnail` aber nicht. Das
             * geht nur auf, solange alle Varianten dieselben Vorschaubilder
             * haben - genau diese Annahme bricht der Konfigurationsfilter in
             * snippets/product-media-gallery.liquid: Wassertank zeigt 12
             * Vorschaubilder, Festwasser 13.
             *
             * Beim Wechsel der Achse "Wasseranschluss" sucht Dawn das
             * Vorschaubild der NEUEN Variante in der noch alten Liste, findet
             * null und wirft. Und weil setActiveMedia aus updateMedia heraus
             * aufgerufen wird, bricht damit der ganze Rest von
             * handleUpdateProductInfo ab: Preis, Lieferzeitraum,
             * Konfigurations-Hinweis und Kaufbutton bleiben auf der vorherigen
             * Variante stehen. Die Seite zeigte 5.199 EUR, in den Warenkorb
             * ging die Variante fuer 5.499 EUR.
             *
             * Gefunden eine Stunde nach dem Launch. Die Pruefung ist dieselbe,
             * die Dawn fuer activeMedia schon macht.
             */
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        // MARO 2026-10-18: siehe oben. Ohne passendes Vorschaubild gibt es
        // nichts zu markieren und keine Position anzusagen - `dataset` auf null
        // waere die zweite Stelle, an der derselbe Fall wirft.
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
